import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiText, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { IntervalUnit } from 'api/interfaces'
import { MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import { useAddMasterCampaignStubCampaignBuilderMutation, useEditMasterCampaignStubCampaignBuilderMutation, useGetMasterCampaignPlanMasterCampaignStubQuery } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { getCampaignTypeNameShort } from 'utils/CampaignType'

import { CampaignFormOld, AddCampaignSectionStepOld, CampaignFormFieldsOld } from './CampaignFormOld'

export enum EditCampaignFlyoutModeOld {
    Add = 'add',
    Edit = 'edit'
}

export interface EditCampaignFlyoutOldProps {
    editMode: EditCampaignFlyoutModeOld
    selectedCampaignBuilderId?: string
    onClose: () => void
}

const EditCampaignFlyoutOld = ({ editMode, selectedCampaignBuilderId, onClose }: EditCampaignFlyoutOldProps) => {
    const { masterCampaignPlanId } = useParams<{ masterCampaignPlanId: string }>()
    const { data: masterCampaignStub } = useGetMasterCampaignPlanMasterCampaignStubQuery({ masterCampaignPlanId: masterCampaignPlanId })
    const [newCampaignStep, setNewCampaignStep] = useState<AddCampaignSectionStepOld>(AddCampaignSectionStepOld.CampaignType)

    const didFireUpdate = useRef(false)

    const [campaignBuilder, setCampaignBuilder] = useState<CampaignFormFieldsOld | undefined>(() => {
        if (editMode === EditCampaignFlyoutModeOld.Add) {
            return {
                name: 'New Campaign',
                shortSummary: '',
                longSummary: '',
                audienceSummary: '',
                geoSummary: '',
                seasonalitySummary: '',
                campaignType: MasterCampaignPlanCampaignBuilderType.TV,
                retargetSite: false,
                startDate: moment().add(3, 'days'),
                intervalUnit: IntervalUnit.Month,
                intervalFrequency: 1,
                intervalDuration: 1,
                segmentTargets: [],
                zips: []
            }
        }

        const builder = masterCampaignStub!.campaignBuilders.find(builder => builder.id === selectedCampaignBuilderId)
        if (!builder) {
            return undefined
        }
        return {
            name: builder.name,
            shortSummary: builder.shortSummary,
            longSummary: builder.longSummary,
            audienceSummary: builder.audienceSummary,
            geoSummary: builder.geoSummary,
            seasonalitySummary: builder.seasonalitySummary,
            campaignType: builder.campaignType,
            intervalBudget: builder.intervalBudget,
            retargetSite: builder.retargetSite,
            startDate: builder.startDate!,
            intervalUnit: builder.intervalUnit || IntervalUnit.Month,
            intervalFrequency: builder.intervalFrequency || 1,
            intervalDuration: builder.intervalDuration || 1,
            segmentTargets: builder.segmentTargets,
            zips: builder.zips || []
        }
    })

    const [editMasterCampaignStubCampaignBuilderMutation, { isSuccess: isEditingMasterCampaignStubCampaignBuilderSuccess, isLoading: isEditingMasterCampaignStubCampaignBuilder }] = useEditMasterCampaignStubCampaignBuilderMutation()
    const [addMasterCampaignStubCampaignBuilderMutation, { isSuccess: isAddingMasterCampaignStubCampaignBuilderSuccess, isLoading: isAddingMasterCampaignStubCampaignBuilder }] = useAddMasterCampaignStubCampaignBuilderMutation()

    const isSuccess = isEditingMasterCampaignStubCampaignBuilderSuccess || isAddingMasterCampaignStubCampaignBuilderSuccess

    useEffect(() => {
        if (didFireUpdate.current && isSuccess) {
            didFireUpdate.current = false
            onClose()
        }
    }, [didFireUpdate, isSuccess])

    if (!campaignBuilder) {
        return null
    }

    const onSave = () => {
        if (!masterCampaignPlanId || !masterCampaignStub || !campaignBuilder.intervalBudget) {
            return
        }
        if (editMode === EditCampaignFlyoutModeOld.Add) {
            addMasterCampaignStubCampaignBuilderMutation({
                masterCampaignStubId: masterCampaignStub.id,
                request: {
                    ...campaignBuilder,
                    intervalBudget: campaignBuilder.intervalBudget
                }
            })
        } else {
            if (!selectedCampaignBuilderId) {
                return
            }
            editMasterCampaignStubCampaignBuilderMutation({
                masterCampaignStubId: masterCampaignStub.id,
                masterCampaignStubCampaignBuilderId: selectedCampaignBuilderId,
                masterCampaignPlanId: masterCampaignPlanId,
                request: {
                    ...campaignBuilder,
                    intervalBudget: campaignBuilder.intervalBudget
                }
            })
        }

        didFireUpdate.current = true
    }

    const onNext = () => {
        setNewCampaignStep(AddCampaignSectionStepOld.CampaignDetails)
    }

    const onBack = () => {
        setNewCampaignStep(AddCampaignSectionStepOld.CampaignType)
    }

    const isSaveDisabled = () => {
        if (!campaignBuilder) return true

        return !campaignBuilder.name || moment(campaignBuilder.startDate).startOf('day').diff(moment().startOf('day'), 'days') < 3 || campaignBuilder.intervalDuration <= 0 || (!!campaignBuilder.intervalBudget && campaignBuilder.intervalBudget < 100) || !campaignBuilder.intervalBudget
    }

    return (
        <AcFlyout onClose={onClose}>
            <EuiFlyoutHeader>
                <EuiTitle size={'m'}>
                    <h2>{editMode === EditCampaignFlyoutModeOld.Add ? `Add Campaign` : `Edit ${getCampaignTypeNameShort(campaignBuilder.campaignType as any)} Campaign`}</h2>
                </EuiTitle>
            </EuiFlyoutHeader>
            <EuiFlyoutBody>{editMode === EditCampaignFlyoutModeOld.Add ? <CampaignFormOld campaignBuilder={campaignBuilder} setCampaignBuilder={setCampaignBuilder} step={newCampaignStep} /> : <CampaignFormOld campaignBuilder={campaignBuilder} setCampaignBuilder={setCampaignBuilder} step={AddCampaignSectionStepOld.CampaignDetails} />}</EuiFlyoutBody>
            <EuiFlyoutFooter>
                {editMode === EditCampaignFlyoutModeOld.Add ? (
                    <EuiFlexGroup direction={'row'} alignItems={'center'}>
                        {newCampaignStep === AddCampaignSectionStepOld.CampaignType && (
                            <>
                                <EuiFlexItem grow={false}>
                                    <EuiButton onClick={onClose}>Cancel</EuiButton>
                                </EuiFlexItem>
                                <EuiFlexItem grow={false}>
                                    <EuiButton fill onClick={onNext}>
                                        Next
                                    </EuiButton>
                                </EuiFlexItem>
                            </>
                        )}
                        {newCampaignStep === AddCampaignSectionStepOld.CampaignDetails && (
                            <>
                                <EuiFlexItem grow={false}>
                                    <EuiButton onClick={onBack}>Back</EuiButton>
                                </EuiFlexItem>
                                <EuiFlexItem grow={false}>
                                    <EuiButton onClick={onSave} isLoading={isAddingMasterCampaignStubCampaignBuilder} fill color={'primary'} isDisabled={isSaveDisabled()}>
                                        Save
                                    </EuiButton>
                                </EuiFlexItem>
                                <EuiFlexItem grow={false}>
                                    <EuiText size='xs'>
                                        <p>* Required fields</p>
                                    </EuiText>
                                </EuiFlexItem>
                            </>
                        )}
                    </EuiFlexGroup>
                ) : (
                    <EuiFlexGroup direction={'row'} alignItems={'center'}>
                        <EuiFlexItem grow={false}>
                            <EuiButton onClick={onClose}>Cancel</EuiButton>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiButton fill onClick={onSave} isLoading={isEditingMasterCampaignStubCampaignBuilder} isDisabled={isSaveDisabled()}>
                                Save
                            </EuiButton>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiText size='xs'>
                                <p>* Required fields</p>
                            </EuiText>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                )}
            </EuiFlyoutFooter>
        </AcFlyout>
    )
}

export default EditCampaignFlyoutOld 